"use strict";

import angular from "angular";

import template from "./table-visualization.component.html";
import "./themes/table-visualization.component-dark.scss";
import "./themes/table-visualization.component.scss";

import _ from "lodash";

import { CalculatingMachine } from "../../../presentation/services/CalculatingMachine/CalculatingMachine";
import { DRE } from "../../../presentation/services/DRE/DRE";

import { Available } from "../../../gateway/Available/Available";
import { Scenario } from "../../../gateway/Scenario/Scenario";

import { AvailablePeriod } from "../../../presentation/services/AvailablePeriod/AvailablePeriod";
import { FormatUnits } from "../../../presentation/services/FormatUnits/FormatUnits";
import * as DateUtils from "../../../utils/date/date";
import { FormatNumbers } from "../../../utils/format-numbers";
import { applyOperatorSign } from "../../../utils/dre-effect";
import { formatHours, HOURS_UNIT } from "../../../utils/format-hours";

class TableVisualizationComponent {
    constructor($stateParams, $filter) {
        this.CalculatingMachine = new CalculatingMachine({});
        this.DRE = new DRE({ CalculatingMachine: this.CalculatingMachine });
        this.AvailablePeriod = new AvailablePeriod();
        this.Available = new Available();
        this.Scenario = new Scenario();
        this.FormatUnits = new FormatUnits($filter);

        this.analysisOptionsAccounting = [
            {
                label: "Receita Bruta",
                value: "RECEITA BRUTA",
                referenceId: "rbc33bB8KFBNznXsX"
            },
            {
                label: "Receita Líquida",
                value: "RECEITA LIQUIDA",
                referenceId: ""
            }
        ];
        this.analysisOptionsFin = [
            {
                label: "Receitas Operacionais",
                value: "RECEITAS OPERACIONAIS",
                referenceId: "Receitas Operacionais"
            },
            {
                label: "Receita Líquida",
                value: "RECEITA LIQUIDA",
                referenceId: "Receita Liquida"
            }
        ];
        this.analysisOptions = [];
        this.analysisOptionSelected = this.analysisOptionsAccounting[0];
        this.rows = [];
        this.columns = [];
        this.companyId = $stateParams.id;
        this.mapAV = {};
        this.mapAVComparation = {};
        this.mapAccumulated = {};
        this.showAV = false;
    }

    $onInit() {
        this._mountData();
    }

    $onChanges() {
        this._mountData();
    }

    _mountData() {
        this.rows = [];
        this.columns = [];
        this._setupPeriodParams();
        this.dates = this._getDates(this.currentPeriod.period);
        this._mountDataSeries();
        this.setAnalysisOptions();
        this.showAV = ["accounting", "fin"].includes(this.source);
        if (!this.showAV) return;
        if (
            this.currentComparation.active &&
            !this.currentComparation.allMonths
        ) {
            this.calcMapAVComparation(
                this.rows,
                this.analysisOptionSelected.value
            );
        } else {
            this.calcMapAV(this.rows, this.analysisOptionSelected.value);
        }
    }

    setAnalysisOptions() {
        if (this.source === "accounting") {
            const receitaLiquida = this.rows.find(
                (r) => r.serie.name_nd === "Receita Líquida"
            );
            this.analysisOptionsAccounting[1].referenceId =
                receitaLiquida.serie._id;
            this.analysisOptions = this.analysisOptionsAccounting;
            this.analysisOptionSelected = this.analysisOptionsAccounting[0];
        } else {
            this.analysisOptions = this.analysisOptionsFin;
            this.analysisOptionSelected = this.analysisOptionsFin[0];
        }
    }

    /**
     * "Todos os meses" e orcamento poem os DOIS lados lado a lado, mes a mes (`point.show`: 0 base,
     * 1 comparado). O Acumulado somava as colunas dos dois anos juntas — um numero que nao e de
     * lado nenhum — e a A.V. saia dele. Aqui cada lado tem o seu Acumulado e a sua A.V.
     *
     * Com `separateTable` a tabela so mostra o lado base: as colunas do comparado ficam de fora.
     */
    _doisLados() {
        return Boolean(this.currentComparation.allMonths || this.budget);
    }

    calcMapAV(rows, analysisOptionSelected) {
        const doisLados = this._doisLados();
        const doLado = (show) => (point) => !doisLados || point.show === show;
        const base = this.calcAccumulatedMap(rows, doLado(0));
        this.mapAccumulated = base;
        this.mapAV = this._mapAVFrom(rows, base, analysisOptionSelected);
        this.showComparationTotals =
            doisLados && !_.get(this.specialConfigs, "separateTable");
        if (this.showComparationTotals) {
            const comparado = this.calcAccumulatedMap(rows, doLado(1));
            this.mapAccumulatedComparation = comparado;
            this.mapAVComparation = this._mapAVFrom(
                rows,
                comparado,
                analysisOptionSelected
            );
        }
    }

    _mapAVFrom(rows, accumulatedMap, analysisOptionSelected) {
        const mapAV = {};
        rows.forEach((row) => {
            // A.V. é "quanto da receita": hora dividida por real não significa nada.
            if (row.serie.unit === HOURS_UNIT) {
                mapAV[row.serie._id] = { pointValue: 0, pointValueFormatted: "-" };
                return;
            }
            if (row.serie.unit === "percentage") {
                mapAV[row.serie._id] = {
                    pointValue: accumulatedMap[row.serie._id].pointValue,
                    pointValueFormatted: FormatNumbers.byUnit(
                        "percentage",
                        accumulatedMap[row.serie._id].pointValue,
                        2
                    )
                };
            } else {
                const serieReference = this.getAccumulatedReference(
                    accumulatedMap,
                    analysisOptionSelected,
                    rows
                );
                const accumulatedReference = serieReference?.pointValue || 0;
                const revenue = +accumulatedReference;
                const accumulated = accumulatedMap[row.serie._id].pointValue;
                const percentage =
                    revenue === 0 ? 0 : (accumulated / revenue) * 100 || 0;
                mapAV[row.serie._id] = {
                    pointValue: percentage,
                    pointValueFormatted: FormatNumbers.byUnit(
                        "percentage",
                        percentage,
                        2,
                        true
                    )
                };
            }
        });
        return mapAV;
    }

    calcMapAVComparation(rows, analysisOptionSelected) {
        const mapAVBase = {};
        const mapAVComparation = {};
        rows.forEach((row) => {
            const pointBase = row.points.find(
                (p) => +p.date === +this.currentPeriod.date
            );
            const pointComparation = row.points.find(
                (p) => +p.date === +this.currentComparation.date
            );
            if (row.serie.unit === HOURS_UNIT) {
                mapAVBase[row.serie._id] = { pointValue: 0, pointValueFormatted: "-" };
                mapAVComparation[row.serie._id] = {
                    pointValue: 0,
                    pointValueFormatted: "-"
                };
                return;
            }
            if (row.serie.unit === "percentage") {
                mapAVBase[row.serie._id] = {
                    pointValue: pointBase.value,
                    pointValueFormatted:
                        analysisOptionSelected === "RECEITA LIQUIDA"
                            ? "-"
                            : pointBase.valueFormatted
                };
                mapAVComparation[row.serie._id] = {
                    pointValue: pointComparation.value,
                    pointValueFormatted:
                        analysisOptionSelected === "RECEITA LIQUIDA"
                            ? "-"
                            : pointComparation.valueFormatted
                };
            } else {
                const serieReference = this.getSerieReference(
                    rows,
                    analysisOptionSelected,
                    this.source
                );
                const pointReferenceBase = serieReference.points.find(
                    (c) => +c.date === +this.currentPeriod.date
                );
                const resultPointBase = this.division(
                    pointBase.value,
                    pointReferenceBase.value
                );
                mapAVBase[row.serie._id] = {
                    pointValue: resultPointBase,
                    pointValueFormatted: FormatNumbers.byUnit(
                        "percentage",
                        resultPointBase,
                        2,
                        true
                    )
                };
                const pointReferenceComparation = serieReference.points.find(
                    (c) => +c.date === +this.currentComparation.date
                );
                const resultPointComparation = this.division(
                    pointComparation.value,
                    pointReferenceComparation.value
                );
                mapAVComparation[row.serie._id] = {
                    pointValue: resultPointComparation,
                    pointValueFormatted: FormatNumbers.byUnit(
                        "percentage",
                        resultPointComparation,
                        2,
                        true
                    )
                };
            }
        });
        this.mapAV = mapAVBase;
        this.mapAVComparation = mapAVComparation;
    }

    getSerieReference(seriesData, reference, source) {
        if (source === "accounting") {
            const referenceId = this.analysisOptionsAccounting.find(
                (option) => option.value === reference
            )?.referenceId;
            const serieReference = seriesData.find(
                (s) => s.serie._id === referenceId
            );
            return serieReference;
        } else {
            const referenceId = this.analysisOptionsFin.find(
                (option) => option.value === reference
            )?.referenceId;
            const serieReference = seriesData.find(
                (s) => s.serie.name_nd === referenceId
            );
            return serieReference;
        }
    }

    division(divisor, dividend) {
        if (dividend) return (divisor / +dividend) * 100 || 0;
        return 0;
    }

    calcAccumulatedMap(rows, doLado = () => true) {
        const accumulatedMap = {};
        const currencyRows = rows.filter(
            (row) => !row.serie.unit || row.serie.unit === "currency"
        );
        currencyRows.forEach((row) => {
            const accumulated = row.points.filter(doLado).reduce(
                (acc, point) => acc + point.value,
                0
            );
            accumulatedMap[row.serie._id] = {
                pointValue: accumulated,
                pointValueFormatted: FormatNumbers.byUnit(
                    "currency",
                    accumulated,
                    2,
                    true
                ),
                valueFormattedNoDecimals: this.FormatUnits.formatValue(
                    "currencyNumber",
                    accumulated,
                    0
                ),
                valueFormatted: this.FormatUnits.formatValue(
                    "currencyNumber",
                    accumulated,
                    2
                )
            };
        });
        // Horas: soma o decimal e formata no fim (1,5 + 0,75 = 2,25 → "2:15").
        rows.filter((row) => row.serie.unit === HOURS_UNIT).forEach((row) => {
            const accumulated = row.points
                .filter(doLado)
                .reduce((acc, point) => acc + (point.value || 0), 0);
            const formatted = formatHours(accumulated);
            accumulatedMap[row.serie._id] = {
                pointValue: accumulated,
                pointValueFormatted: formatted,
                valueFormattedNoDecimals: formatted,
                valueFormatted: formatted
            };
        });
        const percentageRows = rows.filter(
            (row) => row.serie.unit === "percentage"
        );
        percentageRows.forEach((row) => {
            const dividendSerie = rows.find(
                (s) => s.serie._id === row.serie.compound?.[0]
            );
            const dividend = accumulatedMap[dividendSerie.serie._id];
            const dividerSerie = rows.find(
                (s) => s.serie._id === row.serie.compound?.[1]
            );
            const divider = accumulatedMap[dividerSerie.serie._id] || 0;
            const accumulated = (dividend / divider) * 100;
            accumulatedMap[row.serie._id] = {
                pointValue: accumulated,
                pointValueFormatted: FormatNumbers.byUnit(
                    "percentage",
                    accumulated,
                    2
                ),
                valueFormattedNoDecimals: this.FormatUnits.formatValue(
                    "percentage",
                    accumulated,
                    0
                ),
                valueFormatted: this.FormatUnits.formatValue(
                    "percentage",
                    accumulated,
                    2
                )
            };
        });
        return accumulatedMap;
    }

    getAccumulatedReference(accumulatedMap, reference, rows) {
        if (this.source === "accounting") {
            const referenceId = this.analysisOptionsAccounting.find(
                (option) => option.value === reference
            )?.referenceId;
            return accumulatedMap[referenceId];
        } else {
            const referenceId = this.analysisOptionsFin.find(
                (option) => option.value === reference
            )?.referenceId;
            const serieReference = rows.find(
                (s) => s.serie.name_nd === referenceId
            );
            return accumulatedMap[serieReference.serie._id];
        }
    }

    _setupPeriodParams() {
        const available = this.Available.get({
            companyId: this.companyId,
            scenarioId: this.currentPeriod.scenario._id,
            source: this.source,
            options: { budget: this.budget, projection: this.projection }
        });

        const availableDates = available.map((str) => new Date(str));

        this.currentYear = this.currentPeriod.date.getFullYear();
        this.currentMonthIndex = this.currentPeriod.date.getMonth();

        this.minDate = new Date(Math.min(...availableDates));

        this.availableYears = this.Available.getYears(
            this.companyId,
            this.source,
            this.budget || this.currentPeriod.scenario._id,
            { projection: this.projection }
        );
    }

    /**
     * O rótulo sai do nome do cenário sempre que houver um: com a base selecionável junto do
     * Análise Plus, os dois lados podem ser orçamentos, e "Original"/"Orçamento" fixos passariam
     * a mentir — o orçamento escolhido como base apareceria como "Original".
     */
    getComparatioLabel() {
        const budgetName = this._getScenarioName(this.budget);
        if (budgetName) return budgetName;
        if (this.budget) return "Orçamento";
        const comparationName = _.get(this.currentComparation, "scenario.name");
        if (comparationName && !this._isOriginal(this.currentComparation)) {
            return comparationName;
        }
        return "Comparado";
    }

    getOriginalLabel() {
        const periodName = _.get(this.currentPeriod, "scenario.name");
        if (periodName && !this._isOriginal(this.currentPeriod)) {
            return periodName;
        }
        if (this.budget) return "Original";
        return "Base";
    }

    _isOriginal(view) {
        const scenarioId = _.get(view, "scenario._id");
        return !scenarioId || scenarioId === "ORIGINAL";
    }

    _getScenarioName(scenarioId) {
        if (!scenarioId || scenarioId === "ORIGINAL") return "";
        const scenario = this.Scenario?.getById?.(scenarioId);
        return _.get(scenario, "name", "");
    }

    _monthlyDatesComparation() {
        return [
            {
                time: this.currentComparation.date,
                formatted: `${DateUtils.dateToString(
                    this.currentComparation.date,
                    "MMM"
                )}'${DateUtils.dateToString(
                    this.currentComparation.date,
                    "yyyy"
                )} <small>${this.getComparatioLabel()}</small>`,
                scenarioId: this.budget || this.currentComparation.scenario._id
            },
            {
                time: this.currentPeriod.date,
                formatted: `${DateUtils.dateToString(
                    this.currentPeriod.date,
                    "MMM"
                )}'${DateUtils.dateToString(
                    this.currentPeriod.date,
                    "yyyy"
                )} <small>${this.getOriginalLabel()}</small>`,
                scenarioId: this.currentPeriod.scenario._id
            }
        ];
    }

    showCell(type, show) {
        if (!_.get(this.specialConfigs, "separateTable")) return true;

        if (type === "period" && show === 0) return true;

        if (type === "comparation" && show === 1) return true;
    }

    _allMonthsDates() {
        if (this.currentComparation.allMonths)
            return this._allMonthsDatesComparation();
        if (this.budget) return this._allMonthsDatesBudget();
    }

    _allMonthsDatesBudget() {
        const datesAvailable = this.Available.getAvailableByIntervals({
            companyId: this.companyId,
            source: this.source,
            scenarioId: this.currentPeriod.scenario._id,
            startDate: {
                year: this.currentPeriod.startDate.getUTCFullYear(),
                month: this.currentPeriod.startDate.getUTCMonth() + 1
            },
            currentDate: {
                year: this.currentPeriod.date.getUTCFullYear(),
                month: this.currentPeriod.date.getUTCMonth() + 1
            },
            projection: this.projection,
            budget: this.budget,
            options: { type: "allDates" }
        });
        const scenarios = [
            {
                scenarioId: this.currentPeriod.scenario._id,
                thLabel: `<small>${this.getOriginalLabel()}</small>`
            },
            {
                scenarioId: this.budget || this.currentComparation.scenario._id,
                thLabel: `<small>${this.getComparatioLabel()}</small>`
            }
        ];
        const dates = this.isReport ? datesAvailable.slice(-6) : datesAvailable;
        const datesMap = [];
        dates.forEach((date) => {
            scenarios.forEach((scenario, index) => {
                const _date = new Date(date);
                const month = _date.getUTCMonth() + 1;
                const year = _date.getUTCFullYear();
                const regularMonth = String(month).padStart(2, "0");
                const time = new Date(`${year}/${regularMonth}/01`);
                datesMap.push({
                    scenarioId: scenario.scenarioId,
                    time,
                    formatted: `${DateUtils.dateToString(
                        date,
                        "MMM"
                    )}'${DateUtils.dateToString(date, "yyyy")} ${
                        scenario.thLabel
                    }`,
                    show: index
                });
            });
        });
        return datesMap;
    }

    _allMonthsDatesComparation() {
        const years = [
            {
                value: this.currentPeriod.date.getUTCFullYear(),
                scenarioId: this.currentPeriod.scenario._id,
                thLabel: `<small>${this.getOriginalLabel()}</small>`
            },
            {
                value: this.currentComparation.date.getUTCFullYear(),
                scenarioId: this.budget || this.currentComparation.scenario._id,
                thLabel: `<small>${this.getComparatioLabel()}</small>`
            }
        ];
        const months = this.getMonthsAvailable(years);
        const dates = [];
        months.forEach((month) => {
            years.forEach((year, yearIndex) => {
                const regularMonth = String(month.value).padStart(2, "0");

                const date = new Date(`${year.value}/${regularMonth}/01`);

                dates.push({
                    scenarioId: year.scenarioId,
                    time: date,
                    formatted: `${DateUtils.dateToString(
                        date,
                        "MMM"
                    )}'${DateUtils.dateToString(date, "yyyy")} ${year.thLabel}`,
                    show: yearIndex
                });
            });
        });

        return dates;
    }

    getMonthsAvailable(years) {
        let months = [
            { value: 1, label: "Jan" },
            { value: 2, label: "Fev" },
            { value: 3, label: "Mar" },
            { value: 4, label: "Abr" },
            { value: 5, label: "Mai" },
            { value: 6, label: "Jun" },
            { value: 7, label: "Jul" },
            { value: 8, label: "Ago" },
            { value: 9, label: "Set" },
            { value: 10, label: "Out" },
            { value: 11, label: "Nov" },
            { value: 12, label: "Dez" }
        ];
        if (!this.isReport) return months;
        let available = this.Available.getMonths(
            this.companyId,
            years[0].value,
            this.source,
            years[0].scenarioId,
            { budget: this.budget, projection: this.projection }
        );
        let allMonths = available
            .filter((av) => av.available)
            .map((av) => Number(av.monthNumber));
        if (years[0].scenarioId != years[1].scenarioId) {
            const availableComparation = this.Available.getMonths(
                this.companyId,
                years[1].value,
                this.source,
                years[1].scenarioId,
                { budget: this.budget, projection: this.projection }
            );
            const monthsComparation = availableComparation
                .filter((av) => av.available)
                .map((av) => Number(av.monthNumber));
            allMonths = _.uniq([...allMonths, ...monthsComparation]).sort();
        }
        months = months.filter((m) => allMonths.includes(m.value));
        if (months.length >= 6) months = months.slice(-6);
        return months;
    }

    _getDatesComparation(periodInfo) {
        const comparationMonth = this.currentComparation.date.getUTCMonth();
        const comparationYear = this.currentComparation.date.getUTCFullYear();

        const periodMonth = this.currentPeriod.date.getUTCMonth();
        const periodYear = this.currentPeriod.date.getUTCFullYear();

        return [
            {
                time: this.currentComparation.date,
                formatted: `${
                    periodInfo.labels[comparationMonth]
                } ${comparationYear} <small>${this.getComparatioLabel()}</small>`,
                scenarioId: this.budget || this.currentComparation.scenario._id
            },
            {
                time: this.currentPeriod.date,
                formatted: `${
                    periodInfo.labels[periodMonth]
                } ${periodYear} <small>${this.getOriginalLabel()}</small>`,
                scenarioId: this.currentPeriod.scenario._id
            }
        ];
    }

    _quarterlyDatesComparation() {
        const periodInfo = {
            labels: {
                2: "1° T",
                5: "2° T",
                8: "3° T",
                11: "4° T"
            }
        };

        return this._getDatesComparation(periodInfo);
    }

    _semesterDatesComparation() {
        const periodInfo = {
            labels: {
                5: "1° S",
                11: "2° S"
            }
        };

        return this._getDatesComparation(periodInfo);
    }

    _yearlyDatesComparation() {
        const periodInfo = {
            labels: {
                11: ""
            }
        };

        return this._getDatesComparation(periodInfo);
    }

    _yearlyDatesBudget() {
        const periodInfo = {
            months: [11],
            labels: {
                11: ""
            }
        };

        return this._getDatesBudget(periodInfo);
    }

    _semesterDatesBudget() {
        const periodInfo = {
            months: [5, 11],
            labels: {
                5: "1° S",
                11: "2° S"
            }
        };

        return this._getDatesBudget(periodInfo);
    }

    _quarterlyDatesBudget() {
        const periodInfo = {
            months: [2, 5, 8, 11],
            labels: {
                2: "1° T",
                5: "2° T",
                8: "3° T",
                11: "4° T"
            }
        };

        return this._getDatesBudget(periodInfo);
    }

    _getDatesBudget(periodInfo) {
        const datesAvailable = this.Available.getAvailableByIntervals({
            companyId: this.companyId,
            source: this.source,
            scenarioId: this.currentPeriod.scenario._id,
            startDate: {
                year: this.currentPeriod.startDate.getUTCFullYear(),
                month: this.currentPeriod.startDate.getUTCMonth() + 1
            },
            currentDate: {
                year: this.currentPeriod.date.getUTCFullYear(),
                month: this.currentPeriod.date.getUTCMonth() + 1
            },
            projection: this.projection,
            budget: this.budget,
            options: { type: "allDates" }
        });
        const periodDates = this.AvailablePeriod.getClosingDatesByPeriod({
            dates: datesAvailable,
            period: this.currentPeriod.period
        });
        const scenarios = [
            {
                scenarioId: this.currentPeriod.scenario._id,
                thLabel: `<small>${this.getOriginalLabel()}</small>`
            },
            {
                scenarioId: this.budget || this.currentComparation.scenario._id,
                thLabel: `<small>${this.getComparatioLabel()}</small>`
            }
        ];
        const dates = this.isReport ? periodDates.slice(-6) : periodDates;
        const datesMap = [];
        dates.forEach((date) => {
            const _date = new Date(date);
            const month = _date.getUTCMonth();
            const year = _date.getUTCFullYear();
            scenarios.forEach((scenario) => {
                const time = new Date(
                    `${year}/${String(month + 1).padStart(2, 0)}/01`
                );
                const formatted = `${periodInfo.labels[month]}/${year}${scenario.thLabel}`;
                datesMap.push({
                    time,
                    formatted,
                    scenarioId: scenario.scenarioId
                });
            });
        });
        return datesMap;
    }

    _getDates(period) {
        let periodDates = {};

        if (this.currentComparation.allMonths || this.budget) {
            periodDates = {
                monthly: () => this._allMonthsDates(),
                quarterly: () => this._quarterlyDatesBudget(),
                semester: () => this._semesterDatesBudget(),
                yearly: () => this._yearlyDatesBudget()
            };
        } else if (this.currentComparation.active) {
            periodDates = {
                monthly: () => this._monthlyDatesComparation(),
                quarterly: () => this._quarterlyDatesComparation(),
                semester: () => this._semesterDatesComparation(),
                yearly: () => this._yearlyDatesComparation()
            };
        } else {
            periodDates = {
                monthly: () => this._monthlyDates(),
                quarterly: () => this._quarterlyDates(),
                semester: () => this._semesterDates(),
                yearly: () => this._yearlyDates()
            };
        }

        let dates = [];

        if (periodDates[period]) dates = periodDates[period]();

        return dates;
    }

    _mountDataSeries(dates = this.dates) {
        const seriesDRE = this.series.filter((s) => s?.origin !== "goals");
        if (!_.get(seriesDRE, "length")) return;
        for (let index = 0; index < seriesDRE.length; index++) {
            const config = this.configs.find(
                (config) => config.serieId === seriesDRE[index]._id
            );

            /*
             * O `(-)` da linha e o que decide o sinal. Ver `applyOperatorSign`.
             *
             * Linha PERCENTUAL fica de fora: uma margem nao e valor que se subtrai, e nega-la por
             * causa do rotulo trocaria o numero sem que nada na leitura pedisse.
             */
            const comSinal = (valor) =>
                seriesDRE[index].unit === "percentage"
                    ? valor
                    : applyOperatorSign(
                          valor,
                          _.get(config, "label"),
                          seriesDRE[index].name
                      );

            // A META CRUA, para a seta. Ver o comentario em `variationArrow`.
            let metaCrua;

            const row = {
                name: seriesDRE[index].name,
                unit: seriesDRE[index].unit,
                serie: seriesDRE[index]
            };
            if (this.goals) {
                const goalId = this.visualizationData?.goals?.find(
                    (g) => g.s === seriesDRE[index]._id
                );
                let serieGoal = null;
                if (goalId) {
                    serieGoal = this.series.find((s) => {
                        if (this.source === "accounting")
                            return s.referenceId === goalId.r;
                        return s._id === goalId.r;
                    });
                }

                if (!serieGoal) {
                    metaCrua = 0;
                    row.goalValue = 0;
                    row.goalValueFormatted = 0;
                    row.goalValueFormattedNoDecimals = 0;
                } else {
                    // A meta segue o rotulo, como a celula que ela acompanha.
                    metaCrua = this.DRE.getPointGoal({
                        points: this.points,
                        serie: serieGoal
                    });
                    const point = comSinal(metaCrua);
                    const unit =
                        seriesDRE[index].unit === "currency"
                            ? "currencyNumber"
                            : seriesDRE[index].unit;
                    const goalValueFormatted = this.FormatUnits.formatValue(
                        unit,
                        point,
                        2
                    );
                    const goalValueFormattedNoDecimals = this.FormatUnits.formatValue(
                        unit,
                        point,
                        0
                    );
                    row.goalValue = point;
                    row.goalValueFormatted = goalValueFormatted;
                    row.goalValueFormattedNoDecimals = goalValueFormattedNoDecimals;
                }
            }

            const points = dates.map((date) => {
                const pontoCru = this.DRE.getPoint({
                    points: this.points,
                    time: date.time,
                    serie: seriesDRE[index],
                    scenarioId: date.scenarioId
                });

                const point = comSinal(pontoCru);

                const variation = Math.abs(row?.goalValue - point);

                /*
                 * A seta da meta decide pelo valor CRU, que e o que ela sempre comparou.
                 *
                 * Sobre o valor ja com sinal ela inverteria em toda linha `(-)`, cujos dois lados
                 * passam a ser negados juntos. Sobre MAGNITUDE quebraria as outras: linha `(=)` ja
                 * podia vir negativa antes disto — prejuizo —, e `|-50.000| > |10.000|` diria que
                 * a empresa que deu prejuizo superou a meta.
                 */
                const variationArrow = () => {
                    if (pontoCru > metaCrua) return "fa-arrow-up";
                    if (pontoCru < metaCrua) return "fa-arrow-down";
                    if (pontoCru === metaCrua) return "fa-minus";
                };

                const unit =
                    seriesDRE[index].unit === "currency"
                        ? "currencyNumber"
                        : seriesDRE[index].unit;

                const valueFormatted = this.FormatUnits.formatValue(
                    unit,
                    point,
                    2
                );
                const valueFormattedNoDecimals = this.FormatUnits.formatValue(
                    unit,
                    point,
                    0
                );
                // Diferença para o objetivo sai na unidade da série quando é hora; as demais
                // seguem como antes (moeda).
                const variationUnit =
                    seriesDRE[index].unit === HOURS_UNIT
                        ? HOURS_UNIT
                        : "currencyNumber";
                const variationValueFormatted = this.FormatUnits.formatValue(
                    variationUnit,
                    variation,
                    2
                );
                const variationValueFormattedNoDecimals = this.FormatUnits.formatValue(
                    variationUnit,
                    variation,
                    0
                );

                return {
                    valueFormatted,
                    valueFormattedNoDecimals:
                        seriesDRE[index].unit === "percentage"
                            ? valueFormatted
                            : valueFormattedNoDecimals,
                    value: point,
                    show: date.show,
                    projection: date.projection,
                    variationValueFormatted,
                    variationValueFormattedNoDecimals,
                    variation,
                    variationUnit,
                    conditionArrow: variationArrow(),
                    date: date.time
                };
            });

            if (config) row.label = config.label;
            row.points = points;
            this.rows.push(row);
        }
    }

    onTooltipVariation(point) {
        const value =
            point.variationUnit === HOURS_UNIT
                ? formatHours(Math.abs(point.variation))
                : this._toCurrencyBRL(point.variation);
        if (point.conditionArrow === "fa-arrow-up")
            return `${value} acima do objetivo desejado.`;
        if (point.conditionArrow === "fa-arrow-down")
            return `${value} abaixo do objetivo desejado.`;
    }

    _toCurrencyBRL(value) {
        return value.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL"
        });
    }

    _monthlyDates() {
        let datesAvailable = this.Available.getAvailableByIntervals({
            companyId: this.companyId,
            source: this.source,
            scenarioId: this.currentPeriod.scenario._id,
            startDate: {
                year: this.currentPeriod.startDate.getUTCFullYear(),
                month: this.currentPeriod.startDate.getUTCMonth() + 1
            },
            currentDate: {
                year: this.currentPeriod.date.getUTCFullYear(),
                month: this.currentPeriod.date.getUTCMonth() + 1
            },
            projection: this.projection,
            budget: this.budget,
            options: { type: "allDates" }
        });
        const { projectionDates } = this.Available.get({
            companyId: this.companyId,
            source: this.source,
            scenarioId: this.currentPeriod.scenario._id,
            options: { separatted: true, projection: this.projection }
        });
        let dates = [];
        const countDates =
            datesAvailable.length > 6 && this.isReport
                ? 6
                : datesAvailable.length;
        for (let index = 0; index < countDates; index++) {
            const time = DateUtils.getPreviousMonth(
                this.currentPeriod.date,
                index
            );
            const formatted = `${DateUtils.dateToString(
                time,
                "MMM"
            )}'${DateUtils.dateToString(time, "yyyy")}`;
            const projection = projectionDates.find(
                (dt) => dt === time.toISOString()
            );
            dates.push({
                time,
                formatted,
                scenarioId: this.currentPeriod.scenario._id,
                projection
            });
        }
        return dates.reverse();
    }

    isOnlyGoals() {
        return this.goals;
    }

    isGoalsWithProjection() {
        return this.goals && !this.budget && this.projection;
    }

    isGoalsWithBudget() {
        return this.goals && this.budget && !this.projection;
    }

    _quarterlyDates() {
        const periodInfo = {
            months: [2, 5, 8, 11],
            labels: {
                2: "1° T",
                5: "2° T",
                8: "3° T",
                11: "4° T"
            }
        };

        return this._getDatesToTable(periodInfo);
    }

    _semesterDates() {
        const periodInfo = {
            months: [5, 11],
            labels: {
                5: "1° S",
                11: "2° S"
            }
        };

        return this._getDatesToTable(periodInfo);
    }

    _yearlyDates() {
        const periodInfo = {
            months: [11],
            labels: {
                11: ""
            }
        };

        return this._getDatesToTable(periodInfo);
    }

    _getDatesToTable(periodInfo) {
        let datesAvailable = this.Available.getAvailableByIntervals({
            companyId: this.companyId,
            source: this.source,
            scenarioId: this.currentPeriod.scenario._id,
            startDate: {
                year: this.currentPeriod.startDate.getUTCFullYear(),
                month: this.currentPeriod.startDate.getUTCMonth() + 1
            },
            currentDate: {
                year: this.currentPeriod.date.getUTCFullYear(),
                month: this.currentPeriod.date.getUTCMonth() + 1
            },
            projection: this.projection,
            budget: this.budget,
            options: { type: "allDates" }
        });
        const periodDates = this.AvailablePeriod.getClosingDatesByPeriod({
            dates: datesAvailable,
            period: this.currentPeriod.period
        });
        const { projectionDates } = this.Available.get({
            companyId: this.companyId,
            scenarioId: this.currentPeriod.scenario._id,
            source: this.source,
            options: { projection: this.projection, separatted: true }
        });
        const dates = [];
        periodDates.forEach((date) => {
            const _date = new Date(date);
            const time = new Date(
                `${_date.getUTCFullYear()}/${String(
                    _date.getUTCMonth() + 1
                ).padStart(2, 0)}/01`
            );
            const formatted = `${
                periodInfo.labels[_date.getUTCMonth()]
            } ${_date.getUTCFullYear()}`;
            const projection = projectionDates.find((dt) => dt === date);
            dates.push({
                time,
                formatted,
                scenarioId: this.currentPeriod.scenario._id,
                projection
            });
        });
        if (this.isReport) return dates.slice(-6);
        return dates;
    }

    onChangeAnalysis(option) {
        this.analysisOptionSelected = option;
        if (
            this.currentComparation.active &&
            !this.currentComparation.allMonths
        ) {
            this.calcMapAVComparation(this.rows, option.value);
        } else {
            this.calcMapAV(this.rows, option.value);
        }
    }
}

export { TableVisualizationComponent };

TableVisualizationComponent.$inject = ["$stateParams", "$filter"];

const name = "tableVisualizationComponent";
const def = {
    bindings: {
        series: "<",
        configs: "<",
        points: "<",
        currentPeriod: "<",
        currentComparation: "<",
        source: "<",
        specialConfigs: "<",
        budget: "<",
        projection: "<",
        goals: "<",
        visualizationData: "<",
        isReport: "<"
    },
    controller: TableVisualizationComponent,
    template
};
angular.module("nucont.shared").component(name, def);
export default { name, ...def };
